/**
 * Ikon navigasi — SVG inline, bukan emoji.
 *
 * Kenapa diganti dari emoji:
 *
 * 1. Emoji dirender oleh font sistem, jadi bentuknya BEDA di Android, iOS,
 *    Windows, dan Linux. Ikon navigasi yang berubah bentuk antar perangkat
 *    terbaca sebagai "kena glitch", bukan desain.
 * 2. Emoji tidak bisa diwarnai atau diberi ketebalan garis. Nuansa "premium"
 *    yang sekarang diminta justru bergantung pada dua hal itu: garis tipis yang
 *    konsisten, dan warna yang menyatu dengan tema.
 * 3. Emoji juga dibesarkan oleh OS pada ukuran tertentu, sehingga posisinya
 *    bergeser relatif terhadap teks di sebelahnya.
 *
 * Semua SVG memakai `currentColor`, jadi ikon otomatis mengikuti mode
 * terang/gelap dan tidak perlu dua set aset.
 *
 * Semua ikon memakai grid 24x24 dan `stroke-width` yang sama supaya memiliki
 * "bobot" visual yang konsisten saat berdekatan di sidebar.
 */

export type IconName =
  | 'home'
  | 'book'
  | 'cap'
  | 'pen'
  | 'user'
  | 'sparkle'
  | 'heart'
  | 'trophy'
  | 'globe'
  | 'map'
  | 'gear'
  | 'info'
  | 'shield'

/** Path stroke untuk setiap ikon. `fill` dipakai hanya oleh ikon yang terisi. */
const PATHS: Record<IconName, { d: string; fill?: boolean; extra?: string }[]> = {
  // Atap + pintu. Sengaja HANYA garis: versi terisi penuh jadi gumpalan padat di
  // 19px karena pintu bukan path terpisah, melainkan lubang di dalam satu
  // bentuk. State aktif ditandai lewat latar, bukan lewat pengisian ikon.
  home: [
    { d: 'M3.2 10.4 12 3.3l8.8 7.1' },
    { d: 'M5 11.6V19a1.2 1.2 0 0 0 1.2 1.2h11.6A1.2 1.2 0 0 0 19 19v-7.4' },
    { d: 'M9.6 20.2v-5.4h4.8v5.4' },
  ],
  // Buku terbuka dengan garis halaman - lebih jelas daripada tumpukan buku.
  book: [
    { d: 'M12 6.5C10.4 5.2 8.3 4.6 5.5 4.6A1.5 1.5 0 0 0 4 6.1v11.3A1.5 1.5 0 0 0 5.5 19c2.8 0 4.9.6 6.5 1.9 1.6-1.3 3.7-1.9 6.5-1.9a1.5 1.5 0 0 0 1.5-1.6V6.1a1.5 1.5 0 0 0-1.5-1.5c-2.8 0-4.9.6-6.5 1.9z' },
    { d: 'M12 6.5v14.4' },
  ],
  // Toga (topi sarjana).
  //
  // Bentuk sebelumnya memakai diamond yang terlalu tinggi, dan di 19pxdiamond
  // itu + busur bawah menyatu jadi satu siluet yang terbaca sebagai kotak.
  // Yang membuatnya terbaca sebagai toga adalah dua hal: bidang atas yang
  // lebar tapi tipis, dan TALI yang menjatuh ke kanan - tali adalah pembeda
  // yang dikenali orang jauh lebih cepat daripada bentuk capoanya.
  cap: [
    { d: 'M12 5 2.8 9.6 12 14.2l9.2-4.6z' },
    { d: 'M6.6 11.3v4.1c0 1.6 2.4 2.9 5.4 2.9s5.4-1.3 5.4-2.9v-4.1' },
    { d: 'M20.2 10.1v4.6' },
    { d: 'M20.2 14.7a1 1 0 1 1-2 0 1 1 0 0 1 2 0z' },
  ],
  // Pena tulis miring. Bodi pena terisi, garis pisah tetap garis supaya bentuk
  // lancangnya tidak hilang saat state aktif.
  pen: [
    { d: 'M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4z', fill: true },
    { d: 'M14 6l3 3' },
  ],
  // Orang: lingkaran kepala + bahu.
  user: [
    { d: 'M12 12.5a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
    { d: 'M4.5 20.2a7.6 7.6 0 0 1 15 0' },
  ],
  // Kilau empat arah — dipakai sebagai aksen "premium".
  sparkle: [
    { d: 'M12 3.5l1.8 4.9 4.9 1.8-4.9 1.8L12 16.9l-1.8-4.9-4.9-1.8 4.9-1.8z' },
  ],
  // Hati.
  heart: [
    { d: 'M12 20s-7.5-4.3-7.5-9.4A4.3 4.3 0 0 1 12 7.9a4.3 4.3 0 0 1 7.5 2.7C19.5 15.7 12 20 12 20z' },
  ],
  // Piala.
  trophy: [
    { d: 'M7 4h10v5a5 5 0 0 1-10 0z' },
    { d: 'M7 6H4.5v1.5A3.5 3.5 0 0 0 7 11M17 6h2.5v1.5A3.5 3.5 0 0 1 17 11' },
    { d: 'M12 14v3.5M8.5 20h7' },
  ],
  // Perisai centang: untuk tautan panel admin di halaman Profil.
  shield: [
    { d: 'M12 3 5 6v5.5c0 4.2 2.9 8.1 7 9.5 4.1-1.4 7-5.3 7-9.5V6z' },
    { d: 'm9 12 2 2 4-4' },
  ],
  // Peta untuk "Bahasa yang saya ajukan": wilayah, bukan dokumen.
  map: [
    { d: 'M9.2 4.3 3.5 6v13.7l5.7-1.7 5.6 1.7 5.7-1.7V4.3l-5.7 1.7z' },
    { d: 'M9.2 4.3v13.7M14.8 6v13.7' },
  ],
  // Roda gigi.
  gear: [
    { d: 'M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z' },
    { d: 'M19.1 14.4a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.2a1.9 1.9 0 1 1-3.8 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a1.9 1.9 0 1 1 0-3.8h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5V3a1.9 1.9 0 1 1 3.8 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0-.3 1.8v.1a1.6 1.6 0 0 0 1.5 1h.2a1.9 1.9 0 1 1 0 3.8h-.1a1.6 1.6 0 0 0-1.5 1z' },
  ],
  // Huruf i di dalam lingkaran.
  info: [
    { d: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z' },
    { d: 'M12 11v5' },
    { d: 'M12 8v.1' },
  ],
  // Globe: bulatan + garis meridian.
  globe: [
    { d: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z' },
    { d: 'M3.2 9.5h17.6M3.2 14.5h17.6' },
    { d: 'M12 3c2.2 2.4 3.4 5.6 3.4 9s-1.2 6.6-3.4 9c-2.2-2.4-3.4-5.6-3.4-9S9.8 5.4 12 3z' },
  ],
}

/**
 * @param size   ukuran dalam px; mengikuti font berjalan supaya ikon selaras
 *                dengan teks di sebelahnya di sidebar.
 * @param filled ikon terisi penuh (dipakai untuk state aktif) vs. garis saja.
 */
export default function NavIcon({
  name,
  size = 20,
  filled = false,
  className = '',
}: {
  name: IconName
  size?: number
  filled?: boolean
  className?: string
}) {
  const parts = PATHS[name]
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {/*
        `filled` hanya boleh mengisi path yang ditandai `fill: true` di PATHS.
        Kalau semua path ikut terisi, ikon garis (rumah, buku, orang) berubah jadi
        gumpalan padat dan kehilangan semua detail - itu yang membuat versi
        Home terisi Earlier terbaca seperti berantakan.
      */}
      {parts.map((p, i) => {
        const solid = Boolean(p.fill) && filled
        return (
        <path
          key={i}
          d={p.d}
          stroke={solid ? 'none' : 'currentColor'}
          fill={solid ? 'currentColor' : 'none'}
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )})}
    </svg>
  )
}
